import { type InfiniteData, type QueryKey, useInfiniteQuery } from '@tanstack/react-query';
import { isObject } from 'lodash';
import { useMemo } from 'react';
import invariant from 'tiny-invariant';

import { People } from '@air/api';
import { callAirApi } from '@air/utils-api';

import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { type PeopleSortField, type PrivatePeopleListResponse } from '~/swr-hooks/person/types';

export const PRIVATE_PEOPLE_LIST_KEY = 'PRIVATE_PEOPLE_LIST';

export type UseGetPrivatePeopleListParams = {
  search?: string;
  limit?: number;
  sortField?: PeopleSortField;
  sortDirection?: 'desc' | 'asc';
};

type PrivatePeopleListKeyParams = UseGetPrivatePeopleListParams & { workspaceId: string | undefined };

type PrivatePeopleListKey = [typeof PRIVATE_PEOPLE_LIST_KEY, PrivatePeopleListKeyParams];

export const getPrivatePeopleListKey = (params: PrivatePeopleListKeyParams): PrivatePeopleListKey => [
  PRIVATE_PEOPLE_LIST_KEY,
  params,
];

export const isPrivatePeopleListKey = (key: QueryKey): key is PrivatePeopleListKey => {
  return Array.isArray(key) && key[0] === PRIVATE_PEOPLE_LIST_KEY && isObject(key[1]);
};

export type PrivatePeopleListCacheType = InfiniteData<PrivatePeopleListResponse>;

type PersonSearchParams = NonNullable<Parameters<typeof People.search>[0]>;

export const useGetPrivatePeopleList = ({
  search = '',
  limit = 50,
  sortField = 'firstName',
  sortDirection = 'desc',
  enabled = true,
}: UseGetPrivatePeopleListParams & { enabled?: boolean }) => {
  const { currentWorkspace } = useCurrentWorkspace();

  const queryKey = getPrivatePeopleListKey({
    workspaceId: currentWorkspace?.id,
    search,
    limit,
    sortField,
    sortDirection,
  });

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage, refetch, isFetching } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam }) => {
      invariant(currentWorkspace?.id, 'Workspace ID is required to fetch people');

      const searchParams: PersonSearchParams = {
        workspaceId: currentWorkspace?.id,
        search,
        cursor: pageParam || undefined,
        sortField: { name: sortField, direction: sortDirection },
        limit,
      };

      searchParams.filters = { named: { is: true } };

      return callAirApi<PrivatePeopleListResponse>(() => People.search(searchParams));
    },
    getNextPageParam: (lastPage) => lastPage?.pagination?.cursor || null,
    initialPageParam: '',
    enabled: enabled && !!currentWorkspace?.id,
  });

  const flattenedData = useMemo(() => data?.pages.flatMap((page) => page.data) ?? [], [data?.pages]);

  return {
    data: flattenedData,
    isLoading: isLoading || !currentWorkspace?.id,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    total: data?.pages[0]?.total ?? 0,
    refetch,
  };
};
